import React, { useState } from 'react';
import { Icon } from './Icons.jsx';
import { MathText } from './MathText.jsx';
import { DIFFICULTIES } from '@physlab/teaching-agent';

function QuestionCard({ question, index }) {
  const levelLabel = question.difficulty === 'challenge' ? '挑战' : question.difficulty === 'standard' ? '进阶' : '基础';
  return (
    <article className={`question-card level-${question.difficulty}`}>
      <header className="question-head">
        <span className="question-index">{String(index + 1).padStart(2, '0')}</span>
        <div className="question-tags">
          <span className={`q-tag ${question.type}`}>{question.type === 'exercise' ? '练习' : '思考'}</span>
          <span className={`q-level ${question.difficulty}`}>{levelLabel}</span>
          <span className="q-topic">{question.topic}</span>
        </div>
      </header>
      <div className="question-prompt">
        <MathText text={question.prompt} />
      </div>
      <div className="answer-box">
        <div className="answer-label">
          <Icon name="check" size={14} />
          标准答案
        </div>
        <div className="answer-text">
          <MathText text={question.answer} />
        </div>
        <details className="solution-details" open={index === 0}>
          <summary>
            <span>标准解析</span>
            <span className="solution-count">{question.solution.length} 步</span>
            <Icon name="chevron" size={14} />
          </summary>
          <ol className="solution-list">
            {question.solution.map((step, stepIndex) => (
              <li key={`${question.id}-step-${stepIndex}`}>
                <MathText text={step} />
              </li>
            ))}
          </ol>
          <div className="knowledge-row">
            {question.knowledge.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
        </details>
      </div>
    </article>
  );
}

function Skeleton() {
  return (
    <div className="question-skeletons" aria-label="Agent 正在生成">
      {[0, 1, 2].map((item) => (
        <div className="question-skeleton" key={item}>
          <div className="sk-line short" />
          <div className="sk-line" />
          <div className="sk-line" />
          <div className="sk-line medium" />
        </div>
      ))}
    </div>
  );
}

export function AgentPanel({
  model,
  snapshot,
  exerciseSet,
  generating,
  stale,
  difficulty,
  counts,
  onDifficulty,
  onCounts,
  onGenerate,
  onExport,
  onExportStudent,
  selfTest,
  onOpenDiagnostics,
}) {
  const [tab, setTab] = useState('exercises');
  const questions = exerciseSet ? (tab === 'exercises' ? exerciseSet.exercises : exerciseSet.thinking) : [];

  return (
    <div className="panel-scroll agent-panel">
      <div className="agent-hero">
        <div className="agent-orb">
          <Icon name="sparkles" size={23} />
          <span className="orb-ring" />
        </div>
        <div className="agent-hero-copy">
          <div className="eyebrow">TEACHING AGENT</div>
          <h2>把当前实验变成一堂课</h2>
          <p>基于当前参数生成分层练习、思考题与标准解析，并导出可直接打印的 Markdown 实验指导书。</p>
        </div>
        <span className="agent-online">
          <i />
          就绪
        </span>
      </div>

      <div className="agent-status-strip">
        <span>
          <Icon name="cpu" size={13} />
          本地确定性引擎
        </span>
        <span>
          <Icon name="shield" size={13} />
          自检 {selfTest ? selfTest.summary.label : '—'}
        </span>
        <span>
          <Icon name="target" size={13} />
          v1.0
        </span>
        <button type="button" onClick={onOpenDiagnostics}>
          诊断
          <Icon name="chevronRight" size={12} />
        </button>
      </div>

      {stale && (
        <div className="stale-banner">
          <Icon name="alert" size={16} />
          <div>
            <strong>实验参数已更新</strong>
            <span>当前题集仍基于旧参数，重新生成后再导出可保证答案与实验一致。</span>
          </div>
          <button type="button" onClick={onGenerate}>
            重新生成
          </button>
        </div>
      )}

      <section className="agent-controls">
        <div className="control-row">
          <span className="control-label">难度梯度</span>
          <div className="segmented">
            {Object.values(DIFFICULTIES).map((item) => (
              <button
                key={item.key}
                type="button"
                className={difficulty === item.key ? 'active' : ''}
                onClick={() => onDifficulty(item.key)}
                title={item.description}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        <div className="control-row">
          <span className="control-label">题目数量</span>
          <div className="count-controls">
            <label>
              练习
              <span className="mini-segment">
                {[3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={counts.exercises === value ? 'active' : ''}
                    onClick={() => onCounts({ ...counts, exercises: value })}
                  >
                    {value}
                  </button>
                ))}
              </span>
            </label>
            <label>
              思考
              <span className="mini-segment">
                {[2, 3].map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={counts.thinking === value ? 'active' : ''}
                    onClick={() => onCounts({ ...counts, thinking: value })}
                  >
                    {value}
                  </button>
                ))}
              </span>
            </label>
          </div>
        </div>
        <button type="button" className={`agent-generate ${generating ? 'loading' : ''}`} onClick={onGenerate} disabled={generating}>
          <Icon name={generating ? 'refresh' : 'sparkles'} size={17} className={generating ? 'spin' : ''} />
          {generating ? 'Agent 正在计算题目与解析…' : '生成配套练习与解析'}
          <span className="generate-glow" />
        </button>
      </section>

      <section className="export-card">
        <div className="export-copy">
          <span className="export-kicker">
            <Icon name="file" size={13} />
            STANDARD LAB GUIDE
          </span>
          <strong>标准化实验指导书</strong>
          <p>包含实验目的、原理、步骤、参数、记录表、误差分析、题目与完整答案解析。</p>
        </div>
        <div className="export-actions">
          <button type="button" className="export-primary" onClick={onExport} disabled={generating}>
            <Icon name="download" size={16} />
            教师版
          </button>
          <button type="button" className="export-secondary" onClick={onExportStudent} disabled={generating}>
            学生版
          </button>
        </div>
      </section>

      <div className="agent-output">
        <div className="output-head">
          <div className="output-tabs">
            <button
              type="button"
              className={tab === 'exercises' ? 'active' : ''}
              onClick={() => setTab('exercises')}
            >
              课堂练习
              <b>{exerciseSet?.exercises.length ?? counts.exercises}</b>
            </button>
            <button
              type="button"
              className={tab === 'thinking' ? 'active' : ''}
              onClick={() => setTab('thinking')}
            >
              思考题
              <b>{exerciseSet?.thinking.length ?? counts.thinking}</b>
            </button>
          </div>
          <button type="button" className="icon-btn small" onClick={onGenerate} title="重新生成" disabled={generating}>
            <Icon name="refresh" size={15} />
          </button>
        </div>
        <div className="output-summary">
          <span>
            {exerciseSet
              ? `基于 ${Object.keys(snapshot.params).length} 项当前参数 · 种子 ${exerciseSet.seed}`
              : '等待生成'}
          </span>
          <span className="answer-note">
            <Icon name="book" size={13} />
            含标准答案解析
          </span>
        </div>

        {generating ? (
          <Skeleton />
        ) : questions.length > 0 ? (
          <div className="question-list">
            {questions.map((question, index) => (
              <QuestionCard question={question} index={index} key={question.id} />
            ))}
          </div>
        ) : (
          <div className="empty-questions">
            <Icon name="sparkles" size={28} />
            <strong>还没有生成题目</strong>
            <p>选择难度与题量，点击上方按钮让 Agent 根据当前实验参数出题。</p>
          </div>
        )}
      </div>
    </div>
  );
}
